import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  Container,
  Paper,
  Typography,
  Box,
  Grid,
  Button,
  Alert,
  CircularProgress,
  Divider,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Card,
  CardContent
} from '@mui/material';
import {
  ArrowBack,
  LocalShipping,
  CheckCircle,
  AccessTime
} from '@mui/icons-material';
import api from '../services/api';
import { brand } from '../theme/colors';

const OrderDetailPage = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (orderId) {
      loadOrder();
    }
  }, [orderId]);

  const loadOrder = async () => {
    try {
      setLoading(true);
      setError('');

      const response = await api.get(`/material-orders/${orderId}`);

      if (response.data.success) {
        setOrder(response.data.data);
      } else {
        setError('Order not found');
      }
    } catch (err) {
      console.error('Error loading order:', err);
      setError(err.response?.data?.error || 'Failed to load order details');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'confirmed':
        return 'success';
      case 'preparing':
        return 'warning';
      case 'delivered':
        return 'info';
      case 'completed':
        return 'success';
      case 'cancelled':
        return 'error';
      default:
        return 'default';
    }
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4, textAlign: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  if (error || !order) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error" sx={{ mb: 3 }}>
          {error || 'Order not found'}
        </Alert>
        <Button startIcon={<ArrowBack />} onClick={() => navigate(-1)}>
          Go Back
        </Button>
      </Container>
    );
  }

  // Parse delivery address if it's a string
  let deliveryAddress = order.delivery_address;
  if (typeof deliveryAddress === 'string') {
    try {
      deliveryAddress = JSON.parse(deliveryAddress);
    } catch (e) {
      deliveryAddress = { address: deliveryAddress };
    }
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
        <Button startIcon={<ArrowBack />} onClick={() => navigate(-1)}>
          Back
        </Button>
        <Typography variant="h4" sx={{ flexGrow: 1 }}>
          Order Details
        </Typography>
        <Chip
          label={order.status?.replace('_', ' ') || 'Unknown'}
          color={getStatusColor(order.status)}
          icon={order.status === 'delivered' ? <CheckCircle /> : <AccessTime />}
        />
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Order Information */}
        <Grid item xs={12} md={8}>
          <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              Order Information
            </Typography>
            
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary">Order ID</Typography>
              <Typography variant="body1" sx={{ fontFamily: 'monospace' }}>
                {order.id}
              </Typography>
            </Box>

            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary">Project</Typography>
              <Typography variant="body1">{order.project_title || 'N/A'}</Typography>
            </Box>

            {user.role === 'merchant' && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary">Client</Typography>
                <Typography variant="body1">{order.client_name || order.client_email || 'N/A'}</Typography>
              </Box>
            )}

            {user.role === 'client' && order.merchant_name && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary">Merchant</Typography>
                <Typography variant="body1">{order.merchant_name}</Typography>
              </Box>
            )}

            <Divider sx={{ my: 2 }} />

            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              Order Items
            </Typography>

            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Material</TableCell>
                    <TableCell>Quantity</TableCell>
                    <TableCell align="right">Unit Price</TableCell>
                    <TableCell align="right">Total</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {order.items?.map((item, index) => (
                    <TableRow key={index}>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {item.material_name}
                        </Typography>
                        {item.merchant_product_name && (
                          <Typography variant="caption" color="text.secondary">
                            {item.merchant_product_name}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {item.quantity} {item.unit}
                      </TableCell>
                      <TableCell align="right">
                        ${parseFloat(item.unit_price || 0).toFixed(2)}
                      </TableCell>
                      <TableCell align="right">
                        ${parseFloat(item.total_price || 0).toFixed(2)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>

          {/* Delivery Information */}
          {deliveryAddress && (
            <Paper elevation={2} sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                Delivery Information
              </Typography>
              
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary">Delivery Address</Typography>
                <Typography variant="body1">
                  {deliveryAddress.address || 
                   `${deliveryAddress.street || ''}, ${deliveryAddress.suburb || ''}, ${deliveryAddress.city || ''}`.replace(/^,\s*|,\s*$/g, '') ||
                   'N/A'}
                </Typography>
              </Box>

              {order.delivery_instructions && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary">Delivery Instructions</Typography>
                  <Typography variant="body1">{order.delivery_instructions}</Typography>
                </Box>
              )}

              {order.estimated_delivery_date && (
                <Box>
                  <Typography variant="body2" color="text.secondary">Estimated Delivery Date</Typography>
                  <Typography variant="body1">
                    {new Date(order.estimated_delivery_date).toLocaleDateString()}
                  </Typography>
                </Box>
              )}
            </Paper>
          )}
        </Grid>

        {/* Order Summary */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                Order Summary
              </Typography>

              <Box sx={{ mb: 1, display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2">Subtotal:</Typography>
                <Typography variant="body2">${parseFloat(order.subtotal || 0).toFixed(2)}</Typography>
              </Box>

              {parseFloat(order.delivery_fee || 0) > 0 && (
                <Box sx={{ mb: 1, display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2">Delivery Fee:</Typography>
                  <Typography variant="body2">${parseFloat(order.delivery_fee || 0).toFixed(2)}</Typography>
                </Box>
              )}

              {parseFloat(order.offloading_fee || 0) > 0 && (
                <Box sx={{ mb: 1, display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2">Offloading Fee:</Typography>
                  <Typography variant="body2">${parseFloat(order.offloading_fee || 0).toFixed(2)}</Typography>
                </Box>
              )}

              {parseFloat(order.vat_amount || 0) > 0 && (
                <Box sx={{ mb: 1, display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2">VAT:</Typography>
                  <Typography variant="body2">${parseFloat(order.vat_amount || 0).toFixed(2)}</Typography>
                </Box>
              )}

              <Divider sx={{ my: 2 }} />

              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>Total:</Typography>
                <Typography variant="h6" sx={{ fontWeight: 600, color: 'primary.main' }}>
                  ${parseFloat(order.total_amount || 0).toFixed(2)}
                </Typography>
              </Box>

            {order.delivery_method === 'pickup' && order.pickup_code && (
              <>
                <Divider sx={{ my: 2 }} />
                <Box sx={{ mt: 2 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>Pickup Code</Typography>
                  <Alert severity="info" sx={{ mb: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                      Share this code with the person picking up the order:
                    </Typography>
                    <Box
                      sx={{
                        p: 2,
                        bgcolor: 'primary.light',
                        borderRadius: 1,
                        textAlign: 'center',
                        mt: 1
                      }}
                    >
                      <Typography variant="h5" sx={{ fontWeight: 700, letterSpacing: 2, color: 'primary.dark', fontFamily: 'monospace' }}>
                        {order.pickup_code}
                      </Typography>
                    </Box>
                    <Typography variant="caption" sx={{ display: 'block', mt: 1 }}>
                      The pickup person must provide this code or their ID number at the store.
                    </Typography>
                  </Alert>
                </Box>
              </>
            )}

            {order.delivery_method === 'pickup' && order.pickup_person_name && (
              <>
                <Divider sx={{ my: 2 }} />
                <Box sx={{ mt: 2 }}>
                  <Typography variant="body2" color="text.secondary">Pickup Person</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {order.pickup_person_name}
                  </Typography>
                </Box>
              </>
            )}

            <Divider sx={{ my: 2 }} />

            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" color="text.secondary">Payment Status</Typography>
              <Chip
                label={
                  order.payment_status === 'completed' ? 'Completed (Payment Received)' :
                  order.payment_status === 'processing' ? 'Processing (Payment Released)' :
                  order.payment_status === 'released' ? 'Processing (Payment Released)' : // Legacy support
                  order.payment_status?.replace('_', ' ') || 'Unknown'
                }
                color={
                  order.payment_status === 'completed' ? 'success' :
                  order.payment_status === 'processing' || order.payment_status === 'released' ? 'info' :
                  'warning'
                }
                size="small"
                sx={{ mt: 1 }}
              />
            </Box>

              {user.role === 'merchant' && (order.status === 'confirmed' || order.status === 'preparing') && (
                <Button
                  fullWidth
                  variant="contained"
                  startIcon={<LocalShipping />}
                  onClick={() => navigate(`/orders/${order.id}/delivery/submit`)}
                  sx={{
                    mt: 2,
                    background: brand.gradient,
                    '&:hover': {
                      background: brand.gradientHover
                    }
                  }}
                >
                  {order.delivery_method === 'pickup' ? 'Mark as Picked Up' : 'Mark as Delivered'}
                </Button>
              )}

              {user.role === 'client' && 
               (order.status === 'picked_up' || order.status === 'delivered') && 
               !order.delivery_confirmed_by_client && 
               order.payment_status !== 'completed' && 
               order.payment_status !== 'processing' && 
               order.payment_status !== 'released' && (
                <>
                  <Alert severity="warning" sx={{ mt: 2, mb: 2 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                      ⚠️ Action Required: Confirm Delivery
                    </Typography>
                    <Typography variant="body2">
                      Your order has been {order.status === 'picked_up' ? 'picked up' : 'delivered'}. 
                      Please confirm receipt to release payment of ${parseFloat(order.total_amount || 0).toFixed(2)} to the merchant.
                    </Typography>
                    {order.auto_release_scheduled_at && (
                      <Typography variant="caption" sx={{ display: 'block', mt: 1 }}>
                        If you don't confirm within 72 business hours, payment will be automatically released.
                      </Typography>
                    )}
                  </Alert>
                  <Button
                    fullWidth
                    variant="contained"
                    color="success"
                    startIcon={<CheckCircle />}
                    onClick={() => navigate(`/orders/${order.id}/delivery/confirm`)}
                    sx={{
                      mt: 1,
                      background: 'linear-gradient(135deg, #4caf50 0%, #2e7d32 100%)',
                      '&:hover': {
                        background: 'linear-gradient(135deg, #43a047 0%, #1b5e20 100%)'
                      }
                    }}
                  >
                    Confirm Delivery & Release Payment
                  </Button>
                </>
              )}

              {user.role === 'client' && order.delivery_confirmed_by_client && (
                <Alert severity="success" sx={{ mt: 2 }}>
                  <Typography variant="body2">
                    ✓ You have confirmed delivery. Payment of ${parseFloat(order.total_amount || 0).toFixed(2)} will be released to the merchant.
                  </Typography>
                </Alert>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Container>
  );
};

export default OrderDetailPage;

